import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ProjectsLayout } from '@/layouts/projects-layout'
import { AssetsView } from '@/components/assets/AssetsView'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { AssetsPageProps } from '@/types/assets'

interface OrganizationAssetsProps extends AssetsPageProps {
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
      organization_role: string
    }
  }
}

function Index({ organization, folders, assets, current_folder, breadcrumbs, projects }: OrganizationAssetsProps) {
  const { t } = useTranslation('assets')
  const [activeTab, setActiveTab] = useState<'organization' | 'project'>('organization')
  const [selectedProjectId, setSelectedProjectId] = useState<string>(
    projects && projects.length > 0 ? String(projects[0].id) : ''
  )

  return (
    <div className="flex flex-1 flex-col">
      <div className="@container/main flex flex-1 flex-col gap-2">
        <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
          <div className="px-4 lg:px-6">
            {/* Page Header */}
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-foreground">{t('title')}</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {t('description')}
              </p>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'organization' | 'project')}>
              <div className="flex items-center justify-between mb-4">
                <TabsList>
                  <TabsTrigger value="organization">{t('tabs.organization')}</TabsTrigger>
                  <TabsTrigger value="project" disabled={!projects || projects.length === 0}>
                    {t('tabs.project')}
                  </TabsTrigger>
                </TabsList>

                {activeTab === 'project' && projects && projects.length > 0 && (
                  <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="Select project" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {projects.map((project) => (
                        <SelectItem key={project.id} value={String(project.id)}>
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Organization Assets */}
              <TabsContent value="organization" className="mt-0">
                <div className="bg-card rounded-lg border max-h-[600px] flex flex-col overflow-hidden">
                  <AssetsView
                    ownerType="organization"
                    ownerId={organization.id}
                    initialData={{ folders, assets, current_folder, breadcrumbs }}
                    projects={projects}
                    className="min-h-0"
                  />
                </div>
              </TabsContent>

              {/* Project Assets */}
              <TabsContent value="project" className="mt-0">
                {selectedProjectId ? (
                  <div className="bg-card rounded-lg border max-h-[600px] flex flex-col overflow-hidden">
                    <AssetsView
                      key={selectedProjectId}
                      ownerType="project"
                      ownerId={Number(selectedProjectId)}
                      projects={projects}
                      className="min-h-0"
                    />
                  </div>
                ) : (
                  <div className="bg-card rounded-lg border h-64 flex items-center justify-center">
                    <p className="text-muted-foreground">{t('emptyStates.noProjects')}</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  )
}

Index.layout = (page: React.ReactNode) => <ProjectsLayout>{page}</ProjectsLayout>

export default Index
